import type {
  IAuthorizationData,
  IAuthorizeEnrollmentRequest,
  IAuthorizeLdapRequest,
  IAuthorizePasswordRequest,
  IAuthorizeSMSRequest,
  IAuthorizeWebauthnCriteria,
  IPrepareWebauthnAuthorizationOpts,
  IPrepareWebauthnAuthorizationResponse,
  IRequestAuthorizationTokenEnrollmentOpts,
  IRequestAuthorizationTokenEnrollmentResponse,
  IRequestAuthorizationTokenSMSOpts,
  IRequestAuthorizationTokenSMSResponse,
  IRequestRedirectURIOpenIDAuthorizationCriteria,
  IRequestRedirectURIOpenIDAuthorizationResponse,
  ITOTPAuthorizationRequest,
} from "@mne-authentication/api";
import {
  authorizeEnrollment,
  authorizeLdap,
  authorizePassword,
  authorizeSMS,
  authorizeTotp,
  authorizeWebauthn,
  prepareWebauthnAuthorization,
  requestAuthorizationTokenEnrollment,
  requestAuthorizationTokenSMS,
  requestRedirectURIOpenIDAuthorization,
} from "@mne-authentication/api";
import type { IResultstash } from "@mne-core/api";

import { ArgusUtils } from "@mnemonic/pure-utils";
import { handleResponse } from "@mnemonic/redux-utils";

import { getSessionKey } from "../utils/getSessionKey";
import type { IBaseApiParams } from "./baseApi";
import { baseApi } from "./baseApi";

const authorizeApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /* Queries */
    requestAuthorizationTokenSMS: build.query<
      IResultstash<IRequestAuthorizationTokenSMSResponse>,
      IBaseApiParams<IRequestAuthorizationTokenSMSOpts>
    >({
      queryFn: ({ metaOpts }, api) =>
        handleResponse(
          requestAuthorizationTokenSMS({ sessionKey: getSessionKey(api), apiUrl: ArgusUtils.getApiUrl() }),
          metaOpts,
        ),
    }),
    requestAuthorizationTokenEnrollment: build.query<
      IResultstash<IRequestAuthorizationTokenEnrollmentResponse>,
      IBaseApiParams<IRequestAuthorizationTokenEnrollmentOpts>
    >({
      queryFn: ({ metaOpts }, api) =>
        handleResponse(
          requestAuthorizationTokenEnrollment({ sessionKey: getSessionKey(api), apiUrl: ArgusUtils.getApiUrl() }),
          metaOpts,
        ),
    }),
    requestRedirectURIOpenIDAuthorization: build.query<
      IResultstash<IRequestRedirectURIOpenIDAuthorizationResponse>,
      IBaseApiParams<IRequestRedirectURIOpenIDAuthorizationCriteria>
    >({
      queryFn: ({ criteria, metaOpts }, api) =>
        handleResponse(
          requestRedirectURIOpenIDAuthorization(criteria, {
            sessionKey: getSessionKey(api),
            apiUrl: ArgusUtils.getApiUrl(),
          }),
          metaOpts,
        ),
    }),
    prepareWebauthnAuthorization: build.query<
      IResultstash<IPrepareWebauthnAuthorizationResponse>,
      IBaseApiParams<IPrepareWebauthnAuthorizationOpts>
    >({
      queryFn: ({ metaOpts }, api) =>
        handleResponse(
          prepareWebauthnAuthorization({ sessionKey: getSessionKey(api), apiUrl: ArgusUtils.getApiUrl() }),
          metaOpts,
        ),
    }),
    /* Mutations */
    authorizeSMS: build.mutation<IResultstash<IAuthorizationData>, IBaseApiParams<IAuthorizeSMSRequest>>({
      queryFn: ({ criteria, metaOpts }, api) =>
        handleResponse(
          authorizeSMS(criteria, { sessionKey: getSessionKey(api), apiUrl: ArgusUtils.getApiUrl() }),
          metaOpts,
        ),
      invalidatesTags: ["AuthorizationData"],
    }),
    authorizeEnrollment: build.mutation<IResultstash<IAuthorizationData>, IBaseApiParams<IAuthorizeEnrollmentRequest>>({
      queryFn: ({ criteria, metaOpts }, api) =>
        handleResponse(
          authorizeEnrollment(criteria, { sessionKey: getSessionKey(api), apiUrl: ArgusUtils.getApiUrl() }),
          metaOpts,
        ),
      invalidatesTags: ["AuthorizationData"],
    }),
    authorizeLdap: build.mutation<IResultstash<IAuthorizationData>, IBaseApiParams<IAuthorizeLdapRequest>>({
      queryFn: ({ criteria, metaOpts }, api) =>
        handleResponse(
          authorizeLdap(criteria, { sessionKey: getSessionKey(api), apiUrl: ArgusUtils.getApiUrl() }),
          metaOpts,
        ),
      invalidatesTags: ["AuthorizationData"],
    }),

    authorizePassword: build.mutation<IResultstash<IAuthorizationData>, IBaseApiParams<IAuthorizePasswordRequest>>({
      queryFn: ({ criteria, metaOpts }, api) =>
        handleResponse(
          authorizePassword(criteria, { sessionKey: getSessionKey(api), apiUrl: ArgusUtils.getApiUrl() }),
          metaOpts,
        ),
      invalidatesTags: ["AuthorizationData"],
    }),
    authorizeTotp: build.mutation<IResultstash<IAuthorizationData>, IBaseApiParams<ITOTPAuthorizationRequest>>({
      queryFn: ({ criteria, metaOpts }, api) =>
        handleResponse(
          authorizeTotp(criteria, { sessionKey: getSessionKey(api), apiUrl: ArgusUtils.getApiUrl() }),
          metaOpts,
        ),
      invalidatesTags: ["AuthorizationData"],
    }),
    authorizeWebauthn: build.mutation<IResultstash<IAuthorizationData>, IBaseApiParams<IAuthorizeWebauthnCriteria>>({
      queryFn: ({ criteria, metaOpts }, api) =>
        handleResponse(
          authorizeWebauthn(criteria, { sessionKey: getSessionKey(api), apiUrl: ArgusUtils.getApiUrl() }),
          metaOpts,
        ),
      invalidatesTags: ["AuthorizationData"],
    }),
  }),
});

export const {
  useAuthorizeSMSMutation,
  useRequestAuthorizationTokenEnrollmentQuery,
  useAuthorizeEnrollmentMutation,
  useAuthorizeLdapMutation,
  useRequestAuthorizationTokenSMSQuery,
  useRequestRedirectURIOpenIDAuthorizationQuery,
  useAuthorizePasswordMutation,
  useAuthorizeTotpMutation,
  useAuthorizeWebauthnMutation,
  usePrepareWebauthnAuthorizationQuery,
} = authorizeApi;

export default authorizeApi;
